/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { FC, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, eventAction } from "store/store";
import { VariantType, useSnackbar } from "notistack";

/**
 * Dashboard message notification handler component.
 * 
 * Monitors Redux event state for new messages and displays them as snackbar
 * notifications using notistack library.
 * 
 * Features:
 * - Automatic message display when new messages are added to Redux store
 * - Prevents duplicate notifications with the same key
 * - Supports multiple message types (success, error, warning, info, default)
 * - Auto-hides notifications after 5 seconds
 * - Batches multiple rapid messages and displays them all before clearing
 * - Clears messages from Redux store after displaying to prevent memory leaks
 * 
 * This component renders no visible UI but listens to the event slice in
 * Redux and displays notifications in response to state changes.
 * 
 * @returns Null (component renders nothing, only handles side effects)
 */
const DashboardMessages: FC = () => {
  const event = useSelector((state: RootState) => state.event);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const displayedCountRef = useRef<number>(0);
  const clearTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const ml = event.messages.length;
    if (ml > displayedCountRef.current) {
      for (let i = displayedCountRef.current; i < ml; i++) {
          const message = event.messages[i];
          // Validate that the message is a string or React node
          const msgContent = message.message;
          if (typeof msgContent === 'string' || React.isValidElement(msgContent)) {
            enqueueSnackbar(msgContent, {
              variant: (message.messageType || 'default') as VariantType,
              key: message.id,
              autoHideDuration: 5000,
              preventDuplicate: true
            });
          } else if (msgContent !== undefined && msgContent !== null) {
            enqueueSnackbar(String(msgContent), {
              variant: (message.messageType || 'default') as VariantType,
              key: message.id,
              autoHideDuration: 5000,
              preventDuplicate: true
            });
          }
      }
      
      displayedCountRef.current = ml;
      
      if (clearTimeoutRef.current) {
        clearTimeout(clearTimeoutRef.current);
      }
      
      clearTimeoutRef.current = setTimeout(() => {
        dispatch(eventAction.resetAllMessages());
        displayedCountRef.current = 0;
        clearTimeoutRef.current = null;
      }, 100);
    }

    return () => {
      if (clearTimeoutRef.current) {
        clearTimeout(clearTimeoutRef.current);
      }
    };
  }, [event.messages, dispatch, enqueueSnackbar]);

  return null;
};

export default DashboardMessages;

